/* global HSCore */

import React, { useContext, useEffect, useRef, useState } from "react";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import CartBill from "../OrderDetails/CartBill";
import GoogleIcon from "../../svg/Google";
import AppleIcon from "../../svg/Apple";
import PayPalIcon from "../../svg/PayPal";
import LinksIcon from "../../svg/Links";
import AddCardIcon from "../../svg/AddCard";
import VisaIcon from "../../svg/Visa";
import MasterCardIcon from "../../svg/MasterCard";
import DiscoverIcon from "../../svg/Discover";
import UnionPayIcon from "../../svg/UnionPay";
import ExpressIcon from "../../svg/Express";
import JcbIcon from "../../svg/JCB";
import UserContext from "../../../utils/Contexts/UserContext";
import toast from "react-hot-toast";
import axiosInstance from "../../../utils/Requests/axios";
import ThemeContext from "../../../utils/Contexts/ThemeContext";
import creditCardType from "credit-card-type";
import EftPos from "../../svg/EftPos";
import Girocard from "../../svg/Girocard";
import Diners from "../../svg/Diners";
import Unknown from "../../svg/Unknown";
import MasterCardBrand from "../../svg/MasterCardBrand";
import AmericanExpressBrand from "../../svg/AmericanExpressBrand";
import DiscoverBrand from "../../svg/DiscoverBrand";
import JcbBrand from "../../svg/JcbBrand";
import UnionPayBrand from "../../svg/UnionPayBrand";
import VisaBrand from "../../svg/VisaBrand";
import Constant from "../../Constants";
import { Country, State } from "country-state-city";
import { startCase } from "lodash";

function PaymentMethods({
  setActiveStep,
  activeStep,
  cartData,
  guestTotal,
  linkTotal,
  nicheTotal,
  linkInserts,
  nicheEdits,
  guestPosts,
  getCartData,
}) {
  const { t } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const { selectedTheme } = useContext(ThemeContext);
  const { paymentMethodTypes } = Constant();
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState(null);
  const [cartPaymentMethods, setCartPaymentMethods] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [expressMethod, setExpressMethod] = useState("check");
  const [addedCards, setAddedCards] = useState(null);
  const [balance, setBalance] = useState(null);
  const [errors, setErrors] = useState({});
  const [detectedCardType, setDetectedCardType] = useState("");
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [formValues, setFormValues] = useState({
    cardNumber: "",
    nameOnCard: "",
    expirationDate: "",
    securityCode: "",
    address: "",
    optionalAddress: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    isPrimary: false,
    saveCard: false,
  });

  const handleCardDetails = (e) => {
    const { name, value, type, checked } = e.target;

    setFormValues({
      ...formValues,
      [name]: type === "checkbox" ? checked : value,
    });

    if (name === "cardNumber") {
      const sanitizedValue = value.replace(/\D/g, "");
      let formattedValue = sanitizedValue.replace(/(\d{4})(?=\d)/g, "$1-");

      if (formattedValue.length > 19) {
        formattedValue = formattedValue.slice(0, 19);
      }
      setFormValues((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));

      const cardType = sanitizedValue
        ? creditCardType(sanitizedValue)[0]?.niceType || ""
        : "";
      setDetectedCardType(cardType);
    }

    if (name === "nameOnCard") {
      const sanitizedValue = value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, "");
      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "expirationDate") {
      let sanitizedValue = value.replace(/[^0-9]/g, "");
      let formattedValue = "";

      if (sanitizedValue.length === 1) {
        if (sanitizedValue > "1") {
          formattedValue = `0${sanitizedValue}/`;
        } else {
          formattedValue = sanitizedValue;
        }
      } else if (sanitizedValue.length === 2) {
        const month = sanitizedValue.slice(0, 2);
        if (Number(month) > 12) {
          formattedValue = "12/";
        } else {
          formattedValue = `${month}/`;
        }
      } else if (sanitizedValue.length > 2) {
        const month = sanitizedValue.slice(0, 2);
        const year = sanitizedValue.slice(2, 4);

        if (Number(month) > 12) {
          formattedValue = "12/";
        } else {
          formattedValue = `${month}/${year}`;
        }
      }

      if (formattedValue.length > 5) {
        formattedValue = formattedValue.slice(0, 5);
      }

      // Update form values with formatted value
      setFormValues((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    }

    if (name === "securityCode") {
      let sanitizedValue = value.replace(/[^0-9]/g, "");

      if (sanitizedValue.length > 4) {
        sanitizedValue = sanitizedValue.slice(0, 4);
      }

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "address") {
      const sanitizedValue = value.replace(
        /[^A-Za-z0-9À-ÖØ-öø-ÿ'.,#\-/ ]/g,
        ""
      );

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "city") {
      const sanitizedValue = value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, "");

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "state") {
      const sanitizedValue = value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ- ]/g, "");

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "zip") {
      const sanitizedValue = value.replace(/[^A-Za-z0-9 -]/g, "");

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }
  };

  const validateForm = (formValues) => {
    let isValid = true;
    const localErrors = {};

    const fieldValidators = {
      // Matches a 16-digit card number, optionally formatted with spaces or hyphens
      cardNumber: /^(?:\d{16}|\d{4}([ -])\d{4}\1\d{4}\1\d{4})$/,

      // Matches expiration date in MM/YY format
      expirationDate: /^(0[1-9]|1[0-2])\/\d{2}$/,

      // Matches a name with at least two words, allowing accents, apostrophes, and hyphens
      nameOnCard:
        /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}(?: [A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50})+$/,

      // Matches a 3 or 4-digit CVV (for Visa, Mastercard, Amex)
      securityCode: /^\d{3,4}$/,

      // Matches any non-empty address (should be more refined for specific needs)
      address: /^[A-Za-z0-9À-ÖØ-öø-ÿ'.,#\-/ ]{5,100}$/,

      // Matches a city name (letters, spaces, apostrophes, and hyphens, 2 to 50 chars)
      city: /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/,

      // Matches a state name or abbreviation (letters, spaces, hyphens, 2 to 50 chars)
      state: /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/,

      // Matches US and international ZIP/postal codes (4-10 digits, optional "-1234")
      zip: /^[A-Za-z0-9 -]{4,10}$/,

      // Matches any non-empty country name (letters, spaces, hyphens, 2 to 50 chars)
      country: /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/,
    };

    const excludeFields = ["isPrimary", "saveCard", "optionalAddress"];

    for (const field of Object.keys(formValues)) {
      if (excludeFields.includes(field)) {
        continue;
      }

      const value = formValues[field];
      let errorMessage = "";
      if (!value) {
        errorMessage = `${startCase(field)} is required`;
        isValid = false;
      } else if (field === "expirationDate") {
        const expirationDateRegex = /^(0[1-9]|1[0-2])\/\d{2}$/;
        if (!expirationDateRegex.test(value)) {
          errorMessage = "Invalid expiry date. Use mm/yy.";
          isValid = false;
        } else {
          const [month, year] = value.split("/").map(Number);
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear() % 100;
          const currentMonth = currentDate.getMonth() + 1;

          if (
            year < currentYear ||
            (year === currentYear && month < currentMonth)
          ) {
            errorMessage = "Expiration date must be in the future.";
            isValid = false;
          }
        }
      } else if (field === "cardNumber") {
        if (detectedCardType === "" || detectedCardType === null) {
          errorMessage = "Invalid card number format.";
          isValid = false;
        }
      } else if (field === "country") {
        if (!selectedCountry) {
          errorMessage = "Invalid country.";
          isValid = false;
        }
      } else if (field === "state") {
        if (!selectedCountry) {
          errorMessage = "Please select a valid country first.";
          isValid = false;
        } else {
          const state = State.getStatesOfCountry(selectedCountry).find(
            (s) =>
              s.name.toLowerCase() === value.toLowerCase() ||
              s.isoCode.toLowerCase() === value.toLowerCase()
          );

          if (!state) {
            errorMessage = "Invalid state.";
            isValid = false;
          }
        }
      } else if (
        fieldValidators[field] &&
        !fieldValidators[field].test(value)
      ) {
        const errorMessages = {
          cardNumber: "Invalid card number format.",
          expirationDate: "Invalid expiry date. Use mm/yy.",
          securityCode: "Invalid security code.",
        };
        errorMessage =
          errorMessages[field] ||
          `${"Invalid"} ${field.replace(/([A-Z])/g, " $1").toLowerCase()}.`;
        isValid = false;
      }

      if (errorMessage) {
        localErrors[field] = errorMessage;
      }
    }

    setErrors(localErrors);
    return isValid;
  };

  const themeColor = selectedTheme === "dark" ? "#FFFFFF" : "#000000";
  const cardBrandConfig = {
    0: { label: "Unknown", Icon: Unknown },
    1: { label: "American Express", Icon: AmericanExpressBrand },
    2: { label: "Diners Club", Icon: Diners },
    3: { label: "Discover", Icon: DiscoverBrand },
    4: { label: "Eftpos Australia", Icon: EftPos },
    5: { label: "Girocard", Icon: Girocard },
    6: { label: "JCB", Icon: JcbBrand },
    7: { label: "MasterCard", Icon: MasterCardBrand },
    8: { label: "UnionPay", Icon: UnionPayBrand },
    9: { label: "Visa", Icon: VisaBrand },
  };

  const paymentMethods = [
    {
      id: "apple",
      label: t("Apple Pay"),
      Icon: (props) => <AppleIcon {...props} fill={themeColor} />,
      type: paymentMethodTypes.apple,
    },
    {
      id: "google",
      label: t("Google Pay"),
      Icon: GoogleIcon,
      type: paymentMethodTypes.google,
    },
    {
      id: "paypal",
      label: t("PayPal"),
      Icon: PayPalIcon,
      type: paymentMethodTypes.paypal,
    },
    {
      id: "balance",
      label: t("Account Balance"),
      Icon: LinksIcon,
      type: paymentMethodTypes.balance,
    },
    ...(Array.isArray(addedCards)
      ? addedCards.map((card) => ({
          id: `card_${card.uuid}`,
          type: paymentMethodTypes.card,
          label: `xxxx xxxx xxxx ${card.last4}`,
          Icon: (props) => {
            const BrandIcon = cardBrandConfig[card?.brand]?.Icon || null;
            return BrandIcon ? (
              <BrandIcon {...props} fill={themeColor} />
            ) : null;
          },
          card, // Pass the card object for later use
        }))
      : []),
    {
      id: "addCard",
      label: t("Add a New Card"),
      Icon: (props) => <AddCardIcon {...props} fill={themeColor} />,
      type: paymentMethodTypes.addCard,
    },
  ];

  const cards = [
    { id: "Mastercard", Icon: MasterCardIcon },
    { id: "Visa", Icon: VisaIcon },
    { id: "Discover", Icon: DiscoverIcon },
    { id: "UnionPay", Icon: UnionPayIcon },
    { id: "American Express", Icon: ExpressIcon },
    { id: "JCB", Icon: JcbIcon },
  ];

  const addedCardsFetched = useRef(false);
  const getAddedCards = () => {
    if (addedCardsFetched.current) return;
    addedCardsFetched.current = true;
    const url = `/users/${userData?.uuid}/cards`;
    axiosInstance
      .get(url)
      .then((response) => {
        setAddedCards(response?.data);
      })
      .catch(() => {
        toast.error(t("There was an error fetching the saved cards data"));
      });
  };

  const balanceFetched = useRef(false);
  const getBalance = () => {
    if (balanceFetched.current) return;
    balanceFetched.current = true;
    const url = `/users/${userData?.uuid}/wallet/balance/available`;
    axiosInstance
      .get(url)
      .then((response) => {
        setBalance(response?.data);
      })
      .catch(() => {
        toast.error(t("There was an error fetching current balance"));
      });
  };

  const getConfig = () => {
    const url = `/cart/pay/config`;
    axiosInstance
      .get(url)
      .then((response) => {
        setCartPaymentMethods(response?.data?.methods);
      })
      .catch(() => {
        toast.error(t("There was an error fetching data"));
      });
  };

  useEffect(() => {
    getConfig();
    getBalance();
    getAddedCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedMethod) {
      setExpressMethod(null);
    }
  }, [selectedMethod]);

  useEffect(() => {
    if (!expressMethod && selectedMethod) {
      if (["apple", "google"].includes(selectedMethod)) {
        setExpressMethod(selectedMethod);
      }
    }
    if (!expressMethod && cartData?.total <= 0) {
      setSelectedMethod("balance");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod, expressMethod]);

  const paymentMethodsLoading =
    addedCards === null ||
    balance === null ||
    availablePaymentMethods === null ||
    cartPaymentMethods === null;
  const filteredPaymentMethods = paymentMethods
    .filter((method) => {
      if (
        (method.id === "apple" && availablePaymentMethods?.applePay) ||
        (method.id === "google" && availablePaymentMethods?.googlePay)
      ) {
        return true;
      }
      return method.id !== "apple" && method.id !== "google";
    })
    .filter((method) => {
      return cartPaymentMethods?.some(
        (availableMethod) => availableMethod.type === method.type
      );
    })
    .filter((method) => {
      if (method.type === paymentMethodTypes.paypal) {
        const paypalConfig = cartPaymentMethods.find(
          (m) => m.type === paymentMethodTypes.paypal && m.key
        );
        return Boolean(paypalConfig);
      }
      return true;
    });

  useEffect(() => {
    const countryList = Country.getAllCountries().map((c) => ({
      label: c.name,
      value: c.isoCode,
    }));
    setCountries(countryList);
  }, []);

  const countrySelectInitRef = useRef(null);
  useEffect(() => {
    if (
      !countrySelectInitRef.current &&
      countries.length &&
      selectedMethod === "addCard"
    ) {
      HSCore.components.HSTomSelect.init(".js-select-country");
      // eslint-disable-next-line react-hooks/exhaustive-deps
      countrySelectInitRef.current = true;
    }
  }, [countries, selectedMethod]);

  const handleCountryChange = (e) => {
    const select = document.getElementById("js-select-country");
    const control = select.tomselect;
    if (control) {
      setSelectedCountry(e.target.value);
      setFormValues((prevValues) => ({
        ...prevValues,
        country: select.options[select.selectedIndex].text,
      }));
    }
  };

  return (
    <div className="row d-xl-flex">
      <div className="col-12 col-xl flex-grow-1">
        <div className="card mb-4">
          <div className="card-header">
            <h4 className="card-title">{t("Select Payment Method")}</h4>
          </div>
          <div className="d-flex flex-column px-3 p-4 gap-2">
            {paymentMethodsLoading ? (
              <>
                <div className="row g-2">
                  {Array.from({ length: 3 }).map((_, colIndex) => (
                    <div
                      className="col-lg-4 col-md-6"
                      key={colIndex}
                      style={{ position: "relative" }}
                    >
                      <div
                        className="placeholder-glow"
                        style={{ width: "100%" }}
                      >
                        <span
                          className="placeholder col-12 rounded-3"
                          style={{ height: "6.7em" }}
                        ></span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="row g-2">
                  {Array.from({ length: 3 }).map((_, colIndex) => (
                    <div
                      className="col-lg-4 col-md-6"
                      key={colIndex}
                      style={{ position: "relative" }}
                    >
                      <div
                        className="placeholder-glow"
                        style={{ width: "100%" }}
                      >
                        <span
                          className="placeholder col-12 rounded-3"
                          style={{ height: "6.7em" }}
                        ></span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              // Actual Payment Methods
              <>
                {filteredPaymentMethods
                  .reduce((rows, method, index) => {
                    const rowIndex = Math.floor(index / 3);
                    if (!rows[rowIndex]) rows[rowIndex] = [];
                    rows[rowIndex].push(method);
                    return rows;
                  }, [])
                  .map((row, rowIndex) => (
                    <div key={rowIndex} className="row g-2">
                      {row.map(({ id, label, Icon, card }, index) => {
                        const isBalance = id === "balance";
                        const isDisabled = cartData?.total <= 0 && !isBalance;
                        return (
                          <div
                            className="col-lg-4 col-md-6"
                            key={index}
                            style={{ position: "relative" }}
                          >
                            <div
                              onClick={() => {
                                if (!isDisabled) {
                                  setSelectedMethod(id);
                                  countrySelectInitRef.current = null;
                                }
                              }}
                              className={`d-flex flex-column gap-3 rounded-3 px-3 py-3 text-white border 
                              ${
                                selectedMethod === (isBalance ? "balance" : id)
                                  ? "border-primary"
                                  : selectedTheme === "dark"
                                  ? "border-dark"
                                  : "border-transparent"
                              } ${
                                isDisabled
                                  ? "opacity-50 cursor-not-allowed"
                                  : "cursor-pointer"
                              }`}
                              style={{
                                width: "100%",
                                backgroundColor:
                                  selectedMethod ===
                                  (isBalance ? "balance" : id)
                                    ? selectedTheme === "dark"
                                      ? "rgba(45, 50, 54, 1)"
                                      : "rgba(214, 220, 228, 1)"
                                    : selectedTheme === "dark"
                                    ? "rgba(30, 32, 34, 1)"
                                    : "rgba(232, 234, 236, 1)",
                                pointerEvents: isDisabled ? "none" : "auto",
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <Icon />
                                {(selectedMethod === id ||
                                  (isBalance &&
                                    selectedMethod === "balance")) &&
                                  !card?.primary && (
                                    <i
                                      className="bi bi-check2 text-primary fs-3 fw-bold"
                                      style={{
                                        position: "absolute",
                                        right: "18px",
                                      }}
                                    ></i>
                                  )}
                                {card?.primary && (
                                  <span
                                    className="badge bg-soft-primary text-primary"
                                    style={{ paddingTop: "0.2em" }}
                                  >
                                    {t("Primary")}
                                  </span>
                                )}
                              </div>
                              <div className="d-flex align-items-center justify-content-between text-dark card-text">
                                {label}
                                {!!card?.brand && (
                                  <span className="d-block responsive-text-expiry">
                                    {`${card?.exp_month || ""}/${
                                      card?.exp_year?.toString().slice(-2) || ""
                                    }`}
                                  </span>
                                )}
                                {isBalance && (
                                  <span>
                                    $
                                    {balance?.amount?.toLocaleString(
                                      undefined,
                                      {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
              </>
            )}
          </div>

          {selectedMethod === "addCard" && (
            <div className="p-4">
              <h5 className="card-title border-bottom border-2 pb-3">
                {t("Add New Card")}
              </h5>
              <div className="d-flex gap-2 flex-wrap pt-2">
                {cards.map(({ id, Icon }) => (
                  <div
                    key={id}
                    className={`border py-1 px-2 rounded-2 cursor-pointer ${
                      detectedCardType === id
                        ? selectedTheme === "dark"
                          ? "border-primary bg-soft-secondary"
                          : "border-primary bg-dark"
                        : selectedTheme === "dark"
                        ? "bg-transparent"
                        : "bg-soft-dark"
                    }`}
                  >
                    <Icon />
                  </div>
                ))}
              </div>
              <div className="row mt-2">
                {/* Card Details Section */}
                <div className="col-lg-6">
                  <div className="py-3 text-dark">
                    <h5 className="card-title mb-3 border-bottom border-2 pb-2">
                      {t("Card Detail")}
                    </h5>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("Card Number")}
                        name="cardNumber"
                        value={formValues.cardNumber}
                        onChange={handleCardDetails}
                      />
                      {errors.cardNumber && (
                        <div className="text-danger">
                          {t(errors.cardNumber)}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("Name On Card")}
                        name="nameOnCard"
                        value={formValues.nameOnCard}
                        onChange={handleCardDetails}
                      />
                      {errors.nameOnCard && (
                        <div className="text-danger">
                          {t(errors.nameOnCard)}
                        </div>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <input
                          type="text"
                          className="form-control text-dark custom-placeholder rounded-2"
                          placeholder={t("Expiration Date")}
                          name="expirationDate"
                          value={formValues.expirationDate}
                          onChange={handleCardDetails}
                        />
                        {errors.expirationDate && (
                          <div className="text-danger">
                            {t(errors.expirationDate)}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <input
                          type="number"
                          className="form-control text-dark custom-placeholder rounded-2"
                          placeholder={t("Security Code")}
                          name="securityCode"
                          value={formValues.securityCode}
                          onChange={handleCardDetails}
                        />
                        {errors.securityCode && (
                          <div className="text-danger">
                            {t(errors.securityCode)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="primaryCard"
                        name="isPrimary"
                        checked={formValues.isPrimary}
                        onChange={handleCardDetails}
                      />
                      <label className="form-check-label" htmlFor="primaryCard">
                        {t("Make this primary card")}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="saveCard"
                        name="saveCard"
                        checked={formValues.saveCard}
                        onChange={handleCardDetails}
                      />
                      <label className="form-check-label" htmlFor="saveCard">
                        {t("Save my payment details for future purchases")}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Billing Address Section */}
                <div className="col-lg-6">
                  <div className="py-3 text-dark">
                    <h5 className="card-title mb-3 border-bottom border-2 pb-2">
                      {t("Billing Address")}
                    </h5>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("Address")}
                        name="address"
                        value={formValues.address}
                        onChange={handleCardDetails}
                      />
                      {errors.address && (
                        <div className="text-danger">{t(errors.address)}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t(
                          "Optional - Company, C/O, Apt, Suite, Unit"
                        )}
                        name="optionalAddress"
                        value={formValues.optionalAddress}
                        onChange={handleCardDetails}
                      />
                    </div>
                    <div className="row g-2">
                      <div className="col-md-5 mb-3">
                        <input
                          type="text"
                          className="form-control text-dark custom-placeholder rounded-2"
                          placeholder={t("City / Town")}
                          name="city"
                          value={formValues.city}
                          onChange={handleCardDetails}
                        />
                        {errors.city && (
                          <div className="text-danger">{t(errors.city)}</div>
                        )}
                      </div>
                      <div className="col-md-4 mb-3">
                        <input
                          type="text"
                          className="form-control text-dark custom-placeholder rounded-2"
                          placeholder={t("State / Province")}
                          name="state"
                          value={formValues.state}
                          onChange={handleCardDetails}
                        />
                        {errors.state && (
                          <div className="text-danger">{t(errors.state)}</div>
                        )}
                      </div>
                      <div className="col-md-3 mb-3">
                        <input
                          type="number"
                          className="form-control text-dark custom-placeholder rounded-2"
                          placeholder={t("ZIP")}
                          name="zip"
                          value={formValues.zip}
                          onChange={handleCardDetails}
                        />
                        {errors.zip && (
                          <div className="text-danger">{t(errors.zip)}</div>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="tom-select-custom">
                        <select
                          id="js-select-country"
                          className="js-select-country form-select"
                          autoComplete="off"
                          data-hs-tom-select-options={JSON.stringify({
                            placeholder: t("Country"),
                            maxOptions: 250,
                          })}
                          onChange={handleCountryChange}
                        >
                          <option value="">{t("Country")}</option>
                          {countries.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {errors.country && (
                        <div className="text-danger">{t(errors.country)}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <CartBill
        setActiveStep={setActiveStep}
        activeStep={activeStep}
        selectedMethod={selectedMethod}
        setAvailablePaymentMethods={setAvailablePaymentMethods}
        setExpressMethod={setExpressMethod}
        expressMethod={expressMethod}
        cartData={cartData}
        formValues={formValues}
        validateForm={validateForm}
        errors={errors}
        guestTotal={guestTotal}
        linkTotal={linkTotal}
        nicheTotal={nicheTotal}
        linkInserts={linkInserts}
        nicheEdits={nicheEdits}
        guestPosts={guestPosts}
        getCartData={getCartData}
        stripeConfig={cartPaymentMethods?.find((cartPaymentMethod) =>
          [paymentMethodTypes.google, paymentMethodTypes.apple].includes(
            cartPaymentMethod.type
          )
        )}
        paypalConfig={cartPaymentMethods?.find(
          (cartPaymentMethod) =>
            cartPaymentMethod.type === paymentMethodTypes.paypal
        )}
      />
    </div>
  );
}

export default PaymentMethods;
